// ============================================================
// ClientBillingTab.js — Platform-10 per-client billing (admin).
// The BillingEnabled toggle, the per-client PRICE OVERRIDES ("set the
// price per customer"), this client's current-month dry-run preview, and
// annual or monthly MODULE billing (2026-09-30: annual = the modules for 12
// months at 10% off on the term's first bill; seats are always monthly).
// ============================================================
import React, { useCallback, useEffect, useState } from 'react';
import {
  getClientBilling, getRateCard, setClientBillingConfig,
  setClientOverride, clearClientOverride,
  poundsFromPence, penceFromPounds,
} from '../api/billing';
import { toast } from '../components/Toaster';
import StatusMessage from '../components/StatusMessage';
import BcBillingCard from '../components/BcBillingCard';
import './ClientBillingTab.css';

export default function ClientBillingTab({ clientId }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);        // { config, overrides, preview }
  const [rateCard, setRateCard] = useState([]);  // [{ ItemKey, ItemType, Description, PricePence }]
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState({});      // itemKey -> £ string being edited
  const [cycleDraft, setCycleDraft] = useState({ cycle: 'monthly', start: '' });

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [billing, card] = await Promise.all([getClientBilling(clientId), getRateCard()]);
      setData(billing);
      setRateCard(card.items || []);
      const ovr = {};
      (billing.overrides || []).forEach((o) => { ovr[o.ItemKey] = poundsFromPence(o.PricePence); });
      setDrafts(ovr);
      setCycleDraft({
        cycle: (billing.config && billing.config.moduleBillingCycle) || 'monthly',
        start: (billing.config && billing.config.annualTermStart) || '',
      });
    } catch (err) {
      setError(err?.detail || err?.message || 'Failed to load billing.');
    } finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);
  async function toggleEnabled(next) {
    setBusy(true);
    try {
      await setClientBillingConfig(clientId, { billingEnabled: next });
      toast.success(next ? 'Billing enabled for this client.' : 'Billing disabled.');
      await load();
    } catch (err) { toast.error(err?.detail || err?.message || 'Could not update.'); }
    finally { setBusy(false); }
  }

  async function saveModuleBilling() {
    if (cycleDraft.cycle === 'annual' && !/^\d{4}-(0[1-9]|1[0-2])$/.test(cycleDraft.start)) {
      toast.error('Choose the first month of the annual term.');
      return;
    }
    setBusy(true);
    try {
      await setClientBillingConfig(clientId, cycleDraft.cycle === 'annual'
        ? { moduleBillingCycle: 'annual', annualTermStart: cycleDraft.start }
        : { moduleBillingCycle: 'monthly' });
      toast.success(cycleDraft.cycle === 'annual' ? 'Annual module billing saved.' : 'Monthly module billing saved.');
      await load();
    } catch (err) { toast.error(err?.detail || err?.message || 'Could not save.'); }
    finally { setBusy(false); }
  }

  async function saveOverride(itemKey) {
    const pence = penceFromPounds(drafts[itemKey]);
    if (pence == null) { toast.error('Enter a valid price (e.g. 4.50).'); return; }
    setBusy(true);
    try {
      await setClientOverride(clientId, itemKey, { pricePence: pence });
      toast.success('Price override saved.');
      await load();
    } catch (err) { toast.error(err?.detail || err?.message || 'Could not save.'); }
    finally { setBusy(false); }
  }

  async function removeOverride(itemKey) {
    setBusy(true);
    try {
      await clearClientOverride(clientId, itemKey);
      toast.success('Reverted to the default price.');
      await load();
    } catch (err) { toast.error(err?.detail || err?.message || 'Could not clear.'); }
    finally { setBusy(false); }
  }

  if (loading) return <StatusMessage variant="loading" className="cbill__state">Loading billing…</StatusMessage>;
  if (error) return <StatusMessage variant="error" className="cbill__state cbill__state--error">{error} <button className="button-secondary" onClick={load}>Retry</button></StatusMessage>;

  const overrideKeys = new Set((data.overrides || []).map((o) => o.ItemKey));
  const enabled = !!data.config.billingEnabled;
  const preview = data.preview || { lines: [], totalPence: 0 };
  const mb = preview.moduleBilling || null;
  const savedCycle = data.config.moduleBillingCycle || 'monthly';
  const savedStart = data.config.annualTermStart || '';
  const cycleChanged = cycleDraft.cycle !== savedCycle || (cycleDraft.cycle === 'annual' && cycleDraft.start !== savedStart);

  return (
    <div className="cbill">
      <BcBillingCard clientId={clientId} />

      {/* Enable */}
      <div className="cbill__card">
        <div className="cbill__row">
          <div>
            <h3 className="cbill__title">Platform billing</h3>
            <p className="cbill__hint">When enabled, this client is included in the monthly billing preview and snapshot. Business Central billing turns this on by itself when the mandate is signed.</p>
          </div>
          <label className="cbill__switch">
            <input type="checkbox" checked={enabled} disabled={busy} onChange={(e) => toggleEnabled(e.target.checked)} />
            <span>{enabled ? 'Enabled' : 'Disabled'}</span>
          </label>
        </div>
      </div>

      {/* Annual or monthly module billing */}
      <div className="cbill__card">
        <h3 className="cbill__title">Module billing</h3>
        <p className="cbill__hint">
          Monthly: modules are on every monthly bill. Annual: the first bill of the term carries the modules for 12 months
          at 10% off, invoiced in advance on its own invoice; the monthly invoices carry users only. A module added part-way
          through a term is not charged yet (open point; A2Z is alerted). After 12 months the client goes back to monthly
          unless you start a new term. Users are always monthly, in arrears.
        </p>
        <fieldset className="cbill__cycle" disabled={busy}>
          <legend className="cbill__sr">Module billing cycle</legend>
          <label className="cbill__radio">
            <input type="radio" name={`cycle-${clientId}`} value="monthly" checked={cycleDraft.cycle === 'monthly'}
              onChange={() => setCycleDraft((d) => ({ ...d, cycle: 'monthly' }))} />
            <span>Monthly</span>
          </label>
          <label className="cbill__radio">
            <input type="radio" name={`cycle-${clientId}`} value="annual" checked={cycleDraft.cycle === 'annual'}
              onChange={() => setCycleDraft((d) => ({ ...d, cycle: 'annual' }))} />
            <span>Annual (10% off modules)</span>
          </label>
          {cycleDraft.cycle === 'annual' && (
            <label className="cbill__radio">
              <span>First month of the term</span>
              <input className="cbill__input cbill__month" type="month" value={cycleDraft.start}
                onChange={(e) => setCycleDraft((d) => ({ ...d, start: e.target.value }))} />
            </label>
          )}
          <button type="button" className="button-secondary" disabled={busy || !cycleChanged} onClick={saveModuleBilling}>Save</button>
        </fieldset>
        {mb && mb.cycle === 'annual' && (
          <p className="cbill__hint cbill__term">
            {mb.inTerm
              ? `Annual term ${mb.termStart} to ${mb.termEnd}. This month is month ${mb.monthOfTerm} of 12.`
              : `Annual term ${mb.termStart} to ${mb.termEnd}. This month is outside the term, so modules bill monthly.`}
          </p>
        )}
      </div>

      {/* Current-month preview */}
      {enabled && (
        <div className="cbill__card">
          <h3 className="cbill__title">This month ({preview.period}) — preview</h3>
          <table className="cbill__table">
            <thead><tr><th>Line</th><th className="cbill__num">Qty</th><th className="cbill__num">Unit</th><th className="cbill__num">Amount</th><th /></tr></thead>
            <tbody>
              {preview.lines.length === 0 && <tr><td colSpan={5} className="cbill__muted">No charges (no enabled products or billable seats).</td></tr>}
              {preview.lines.map((l) => (
                <tr key={l.itemKey}>
                  <td>{l.description}</td>
                  <td className="cbill__num">{l.quantity}</td>
                  <td className="cbill__num">£{poundsFromPence(l.unitPricePence)}</td>
                  <td className="cbill__num">£{poundsFromPence(l.amountPence)}</td>
                  <td>{l.priceSource === 'override' && <span className="cbill__badge">custom</span>}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr><td colSpan={3} /><td className="cbill__num cbill__total">£{poundsFromPence(preview.totalPence)}</td><td /></tr></tfoot>
          </table>
          {/* Linked clients (2026-10-28): a person with a login in more than one
              linked client is charged once, on the main client. */}
          {preview.linkedSeats && preview.linkedSeats.sharedPeople > 0 && (
            <p className="cbill__hint" data-testid="linked-seats-note">
              {preview.linkedSeats.isMain
                ? `${preview.linkedSeats.sharedPeople} ${preview.linkedSeats.sharedPeople === 1 ? 'person works' : 'people work'} at more than one linked client. As the main client, this client is charged one seat for each, at the most expensive role they hold.`
                : `${preview.linkedSeats.sharedPeople} ${preview.linkedSeats.sharedPeople === 1 ? 'person also works' : 'people also work'} at a linked client. Their seat is charged on the main client, so they are not counted here.`}
            </p>
          )}
        </div>
      )}

      {/* Per-client price overrides */}
      <div className="cbill__card">
        <h3 className="cbill__title">Pricing for this client</h3>
        <p className="cbill__hint">Leave a row on the default, or set a custom price for this customer. Custom prices override the platform default.</p>
        <table className="cbill__table">
          <thead><tr><th>Item</th><th className="cbill__num">Default</th><th className="cbill__num">This client (£)</th><th /></tr></thead>
          <tbody>
            {rateCard.map((it) => {
              const isOverridden = overrideKeys.has(it.ItemKey);
              return (
                <tr key={it.ItemKey}>
                  <td>{it.Description} {isOverridden && <span className="cbill__badge">custom</span>}</td>
                  <td className="cbill__num">£{poundsFromPence(it.PricePence)}</td>
                  <td className="cbill__num">
                    <input className="cbill__input" type="text" inputMode="decimal" placeholder={poundsFromPence(it.PricePence)}
                      value={drafts[it.ItemKey] ?? ''} disabled={busy}
                      onChange={(e) => setDrafts((d) => ({ ...d, [it.ItemKey]: e.target.value }))} />
                  </td>
                  <td className="cbill__actions">
                    <button className="button-secondary" disabled={busy} onClick={() => saveOverride(it.ItemKey)}>Save</button>
                    {isOverridden && <button className="button-link" disabled={busy} onClick={() => removeOverride(it.ItemKey)}>Reset</button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

    </div>
  );
}
